import React, { useEffect, useRef, useState } from "react";
import { Box } from "@material-ui/core";
import { ShapeEditor, ImageLayer, DrawLayer } from "react-shape-editor";
import { useAnnotationPainter, useAnnotationsHandler, useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { MIN_ZOOM, SCALE_FACTOR } from "../..";
import { LoadingOverlay } from "components";
import { useSelector } from "react-redux";
import { getAdMaterialSelectedPageById } from "store/features/materialSpecs/specs";
import { useAdMaterialVisual } from "hooks/api/useAdMaterialVisual";
import { API } from "api";

export const PdfPreview = () => {
    const [isPdfLoading, setPdfLoading] = useState(true);
    const container = useRef<HTMLDivElement>(null);
    const [pdfDimensions, setPdfDimensions] = useState<{ height: number; width: number }>({
        height: 0,
        width: 0,
    });
    const { scale, changeScale } = useAnnotationsHandler();
    const { drawHandler, shapes, addShapeHandler } = useAnnotationPainter();
    const { selectedMaterialSpecId, selectedMaterialId, isVisualVisible, isPrinted } = useMaterialSpecs();
    const materialPages = useSelector(getAdMaterialSelectedPageById(selectedMaterialSpecId, selectedMaterialId));
    const visualAsset = useAdMaterialVisual(isPrinted, selectedMaterialSpecId, selectedMaterialId);

    // scales pdf on open, if too big to fit the container
    useEffect(() => {
        const { width, height } = pdfDimensions;

        if (width !== 0 && height !== 0 && container.current) {
            const { offsetWidth, offsetHeight } = container.current;
            let newWidth = width;
            let newHeight = height;
            let newScale = scale;

            while ((newWidth > offsetWidth || newHeight > offsetHeight) && newScale > MIN_ZOOM) {
                newScale -= SCALE_FACTOR;
                newWidth = width * newScale;
                newHeight = height * newScale;
            }

            if (newScale !== scale) {
                changeScale(newScale);
            }
        }

        // eslint-disable-next-line
    }, [pdfDimensions, changeScale]);

    return materialPages ? (
        <Box overflow="auto" height="100%" {...{ ref: container }}>
            <Box
                display="flex"
                flex={1}
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%">
                <LoadingOverlay active={isPdfLoading}>
                    <ShapeEditor vectorWidth={pdfDimensions.width} vectorHeight={pdfDimensions.height} scale={scale}>
                        <ImageLayer
                            src={
                                isVisualVisible && visualAsset?.bigPreview
                                    ? `${API.serverUrl}${visualAsset.bigPreview.url}`
                                    : materialPages.pages[materialPages.selectedPage].url
                            }
                            onLoad={({ naturalWidth, naturalHeight }) => {
                                setPdfLoading(false);
                                setPdfDimensions((dimensions) => {
                                    if (naturalWidth === dimensions.width && naturalHeight === dimensions.height) {
                                        return dimensions;
                                    }

                                    return {
                                        width: naturalWidth,
                                        height: naturalHeight,
                                    };
                                });
                            }}
                        />
                        <DrawLayer
                            onDrawStart={({ startCorner }) => drawHandler(startCorner)}
                            onAddShape={addShapeHandler}
                        />
                        {shapes}
                    </ShapeEditor>
                </LoadingOverlay>
            </Box>
        </Box>
    ) : null;
};
